<template>
  <RtCard
    icon="Technology-Fill-Preset"
    :title="t('rt.tabs.config')"
    :loading="loading"
    class="gap-3 pr-1.5"
  >
    <template #header-title-action></template>

    <!-- 左侧配置卡片列表 -->
    <div
      ref="scrollBoundaryRef"
      class="rt-config-scrollbar h-full w-full overflow-auto rounded-3xl"
    >
      <Transition name="fade" type="transition" :appear="true">
        <div v-if="!isLoadingPresets" class="flex flex-wrap gap-2 fade-in">
          <Tooltip
            v-if="canAddConfig"
            :disabled="isLoggedIn"
            :content="t('rt.presets.loginRequired')"
            side="bottom"
          >
            <div
              class="flex shrink-0 items-center justify-center size-39 rounded-3xl bg-key text-text-glass-default transition-colors"
              :class="
                isLoggedIn
                  ? 'cursor-pointer hover:text-text-glass-hover active:text-brand-blue2'
                  : 'cursor-not-allowed opacity-60'
              "
              @click="handleAddConfig"
            >
              <Icon icon="Function-Wireframe-Add11" class="size-18" />
            </div>
          </Tooltip>

          <Tooltip
            v-for="config in currentConfigs"
            :key="config.index"
            side="top"
            :delay-duration="150"
            :collision-boundary="scrollBoundaryRef"
            :collision-padding="{ top: -60 }"
            :hide-when-detached="true"
          >
            <Card
              :id="String(config.index)"
              :ref="(instance) => setConfigCardRef(config.index, instance)"
              bg-img-prefix="devices/rt"
              :name="presetName(config)"
              :show-edit="false"
              :show-delete="false"
              :draggable="false"
              class="size-39"
              @click="handleConfigSelect(config.index)"
            >
              <template #tags>
                <div class="w-full flex justify-start items-end text-s-medium min-h-6">
                  <Icon icon="Technology-Fill-Preset" class="size-6" />
                  <span>{{ config.index }}</span>
                </div>
              </template>
              <template #actions>
                <template v-if="config.source === 'user'">
                  <Tooltip :content="t('rt.presets.overwriteTips')" side="bottom">
                    <Button
                      variant="fill"
                      shape="circle"
                      size="icon-xs"
                      color="text-b-b"
                      @click.stop="openOverwriteDialog(config)"
                    >
                      <Icon icon="Multimedia-Wireframe-Paste" class="size-4.5" />
                    </Button>
                  </Tooltip>

                  <Tooltip :content="t('rt.presets.renameTitle')" side="bottom">
                    <Button
                      variant="fill"
                      shape="circle"
                      size="icon-xs"
                      color="text-b-b"
                      @click.stop="openRenameDialog(config)"
                    >
                      <Icon icon="Writing-Wireframe-Edit" class="size-4.5" />
                    </Button>
                  </Tooltip>

                  <Tooltip :content="t('rt.presets.deleteTitle')" side="bottom">
                    <Button
                      variant="fill"
                      shape="circle"
                      size="icon-xs"
                      color="text-b-b"
                      @click.stop="openDeleteDialog(config)"
                    >
                      <Icon icon="Function-Wireframe-Delete" class="size-4.5" />
                    </Button>
                  </Tooltip>
                </template>
              </template>
            </Card>

            <template #content>
              <RtPresetDetails :preset="config" variant="hover" />
            </template>
          </Tooltip>
        </div>
      </Transition>
    </div>
  </RtCard>

  <Dialog
    v-model:open="isCreateDialogOpen"
    :title="t('rt.presets.createTitle')"
    content-class="w-90"
    :close-on-overlay-click="false"
  >
    <div>
      <Input
        v-model="presetNameInput"
        :placeholder="t('rt.presets.namePlaceholder')"
        class="text-black-white"
        @keydown.enter="confirmCreate"
      />
      <div v-if="!isValidName" class="mt-1 text-xs text-red-500">
        {{ $t('home.config.renameDialog.overLimitTip') }}
      </div>
    </div>
    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <Button color="text-b-w" :disabled="isOperating" @click="isCreateDialogOpen = false">
          {{ t('common.cancel') }}
        </Button>
        <Button color="black" :disabled="isOperating || !isValidName" @click="confirmCreate">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:open="isOverwriteDialogOpen"
    :title="t('rt.presets.overwriteTitle')"
    content-class="w-90"
    :restore-focus="false"
  >
    <p class="text-s-medium text-text-glass-default">
      {{
        t('rt.presets.overwriteCloudConfirm', { name: actionPreset && presetName(actionPreset) })
      }}
    </p>
    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <Button color="text-b-w" :disabled="isOperating" @click="closeActionDialogs">
          {{ t('common.cancel') }}
        </Button>
        <Button color="black" :disabled="isOperating" @click="confirmOverwrite">
          {{ t('rt.presets.overwrite') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:open="isRenameDialogOpen"
    :title="t('rt.presets.renameTitle')"
    content-class="w-90"
    :close-on-overlay-click="false"
    :restore-focus="false"
  >
    <div>
      <Input
        v-model="presetNameInput"
        :placeholder="t('rt.presets.namePlaceholder')"
        class="text-black-white"
        @keydown.enter="confirmRename"
      />
      <div v-if="!isValidName" class="mt-1 text-xs text-red-500">
        {{ $t('home.config.renameDialog.overLimitTip') }}
      </div>
    </div>
    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <Button color="text-b-w" :disabled="isOperating" @click="isRenameDialogOpen = false">
          {{ t('common.cancel') }}
        </Button>
        <Button color="black" :disabled="isOperating || !isValidName" @click="confirmRename">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:open="isDeleteDialogOpen"
    :title="t('rt.presets.deleteTitle')"
    content-class="w-90"
    :restore-focus="false"
  >
    <p class="text-s-medium text-text-glass-default">
      {{ t('rt.presets.deleteConfirm', { name: actionPreset && presetName(actionPreset) }) }}
    </p>
    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <Button color="text-b-w" :disabled="isOperating" @click="closeActionDialogs">
          {{ t('common.cancel') }}
        </Button>
        <Button color="black" :disabled="isOperating" @click="confirmDelete">
          {{ t('common.delete') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:open="isApplyDialogOpen"
    :title="
      selectedPreset
        ? t('rt.presets.applyTitle', { name: presetName(selectedPreset) })
        : t('rt.presets.applyPreset')
    "
    content-class="w-auto max-w-none!"
    :close-on-overlay-click="false"
  >
    <template v-if="selectedPreset">
      <p class="text-s-medium text-text-glass-default">
        {{ t('rt.presets.overwriteConfirm') }}
      </p>
      <RtPresetDetails :preset="selectedPreset" variant="dialog" class="mt-2" />
    </template>

    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <Button color="text-b-w" :disabled="isApplying" @click="closeApplyDialog">
          {{ t('common.cancel') }}
        </Button>
        <Button color="black" :disabled="isApplying || !selectedPreset" @click="confirmApply">
          <Icon v-if="isApplying" icon="Function-Wireframe-CW" class="size-4 animate-spin" />
          {{ isApplying ? t('rt.presets.applying') : t('rt.presets.confirmApply') }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, shallowRef, watch, type ComponentPublicInstance } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Button, Dialog, Icon, Input, Tooltip, toast } from '@rapoo/element'
  import RtCard from '../RtCard.vue'
  import Card from '@/views/header/components/configurations/Card.vue'
  import { RT_PRESET_CONFIGS } from './presets'
  import RtPresetDetails from './RtPresetDetails.vue'
  import type { RtPresetConfig } from '../../types'
  import {
    MAX_USER_RT_PRESETS,
    useRtCloudPresets,
  } from '@/views/device/mouse/composables/useRtCloudPresets'

  const props = defineProps<{
    loading?: boolean
    applyPreset: (preset: RtPresetConfig) => Promise<void>
    getCurrentPreset: () => Promise<RtPresetConfig>
  }>()

  const { t } = useI18n()

  const selectedPreset = shallowRef<RtPresetConfig | null>(null)
  const scrollBoundaryRef = ref<HTMLElement | null>(null)
  const isApplyDialogOpen = ref(false)
  const isApplying = ref(false)

  const {
    isLoggedIn,
    configType,
    userPresets,
    isOperating,
    isLoadingPresets,
    loadUserPresets,
    createUserPreset,
    overwriteUserPreset,
    deleteUserPreset,
    renameUserPreset,
  } = useRtCloudPresets()

  /** 官方预设固定在前，用户预设紧随其后；所有卡片统一使用连续 index。 */
  const currentConfigs = computed<RtPresetConfig[]>(() => [
    ...RT_PRESET_CONFIGS.map((preset, index) => ({
      ...preset,
      index: index + 1,
      source: 'official' as const,
    })),
    ...userPresets.value.map((preset, index) => ({
      ...preset,
      index: RT_PRESET_CONFIGS.length + index + 1,
    })),
  ])

  const canAddConfig = computed(() => userPresets.value.length < MAX_USER_RT_PRESETS)
  const isCreateDialogOpen = ref(false)
  const isOverwriteDialogOpen = ref(false)
  const isRenameDialogOpen = ref(false)
  const isDeleteDialogOpen = ref(false)
  const presetNameInput = ref('')

  const isValidName = computed(() => {
    return presetNameInput.value?.trim().length <= 10
  })

  const actionPreset = shallowRef<RtPresetConfig | null>(null)

  type ConfigCardInstance = ComponentPublicInstance & {
    getElement: () => HTMLElement | null
    highlightTemporarily: (hoverDuration?: number) => void
  }
  const configCardRefs = new Map<number, ConfigCardInstance>()

  const setConfigCardRef = (index: number, instance: Element | ComponentPublicInstance | null) => {
    if (instance) {
      configCardRefs.set(index, instance as ConfigCardInstance)
      return
    }
    configCardRefs.delete(index)
  }

  /** 将指定配置卡片滚动到完整可见位置，并短暂展示 hover 状态。 */
  const focusConfigCard = async (index: number) => {
    await nextTick()
    const card = configCardRefs.get(index)
    card?.getElement()?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    })
    card?.highlightTemporarily()
  }

  defineExpose({ focusConfigCard })

  const presetName = (preset: RtPresetConfig) =>
    preset.name ?? (preset.nameKey ? t(preset.nameKey) : t('rt.presets.untitled'))

  const readCurrentPreset = async () => {
    try {
      return await props.getCurrentPreset()
    } catch {
      toast.warning(t('rt.presets.currentUnavailable'))
      return null
    }
  }

  const handleAddConfig = () => {
    if (!isLoggedIn.value || props.loading) return
    presetNameInput.value = t('rt.presets.defaultUserName', {
      index: userPresets.value.length + 1,
    })
    isCreateDialogOpen.value = true
  }

  const handleConfigSelect = (index: number) => {
    if (props.loading || isApplying.value) return
    selectedPreset.value = currentConfigs.value.find((config) => config.index === index) ?? null
    isApplyDialogOpen.value = selectedPreset.value !== null
  }

  const confirmCreate = async () => {
    const name = presetNameInput.value.trim()
    if (!name || isOperating.value) return
    const currentPreset = await readCurrentPreset()
    if (!currentPreset) return
    const createdPreset = await createUserPreset(name, currentPreset)
    if (!createdPreset?.cloudId) return

    isCreateDialogOpen.value = false
    const createdConfig = currentConfigs.value.find(
      (config) => config.cloudId === createdPreset.cloudId,
    )
    if (createdConfig) await focusConfigCard(createdConfig.index)
  }

  const openOverwriteDialog = (preset: RtPresetConfig) => {
    actionPreset.value = preset
    isOverwriteDialogOpen.value = true
  }

  const openRenameDialog = (preset: RtPresetConfig) => {
    actionPreset.value = preset
    isRenameDialogOpen.value = true
    presetNameInput.value = preset.name ?? ''
  }

  const openDeleteDialog = (preset: RtPresetConfig) => {
    actionPreset.value = preset
    isDeleteDialogOpen.value = true
  }

  const closeActionDialogs = () => {
    if (isOperating.value) return
    isOverwriteDialogOpen.value = false
    isDeleteDialogOpen.value = false
    isRenameDialogOpen.value = false
    actionPreset.value = null
  }

  const confirmOverwrite = async () => {
    const cloudId = actionPreset.value?.cloudId
    const configIndex = actionPreset.value?.index
    if (!cloudId || configIndex === undefined || isOperating.value) return
    const currentPreset = await readCurrentPreset()
    if (!currentPreset) return
    if (await overwriteUserPreset(cloudId, currentPreset)) {
      closeActionDialogs()
      await focusConfigCard(configIndex)
    }
  }

  const confirmRename = async () => {
    const cloudId = actionPreset.value?.cloudId
    const configIndex = actionPreset.value?.index
    if (!cloudId || configIndex === undefined || isOperating.value) return
    if (await renameUserPreset(cloudId, presetNameInput.value)) {
      closeActionDialogs()
      await focusConfigCard(configIndex)
    }
  }

  const confirmDelete = async () => {
    const cloudId = actionPreset.value?.cloudId
    if (!cloudId || isOperating.value) return
    if (await deleteUserPreset(cloudId)) closeActionDialogs()
  }

  watch([isLoggedIn, configType], loadUserPresets, { immediate: true })

  const closeApplyDialog = () => {
    if (isApplying.value) return
    isApplyDialogOpen.value = false
    selectedPreset.value = null
  }

  const confirmApply = async () => {
    const preset = selectedPreset.value
    if (!preset || isApplying.value) return

    isApplying.value = true
    try {
      await props.applyPreset(preset)
      toast.success(t('rt.presets.applied', { name: presetName(preset) }))
      isApplyDialogOpen.value = false
      selectedPreset.value = null
    } catch {
      toast.error(t('rt.presets.applyFailed', { name: presetName(preset) }))
    } finally {
      isApplying.value = false
    }
  }
</script>

<style lang="css" scoped>
  .rt-config-scrollbar::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  .rt-config-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .rt-config-scrollbar::-webkit-scrollbar-button {
    -webkit-appearance: none;
    appearance: none;
    display: none;
    width: 0;
    height: 0;
    background: transparent;
  }

  .rt-config-scrollbar::-webkit-scrollbar-thumb {
    background-color: var(--color-b-trans10);
    border-radius: 0.5rem;
  }

  .rt-config-scrollbar::-webkit-scrollbar-corner {
    background: transparent;
  }

  @supports (-moz-appearance: none) {
    .rt-config-scrollbar {
      scrollbar-color: var(--color-b-trans10) transparent;
      scrollbar-width: thin;
    }
  }

  /* we will explain what these classes do next! */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
  }
</style>
